import { extractAvgColor } from "../utilities/color";
import { ColorModule } from "./color.module";
import { DebounceModule } from "./debounce.module";

export class JumbotronColorModule extends ColorModule {
  constructor(jumbotron, extraConfiguration = {}) {
    super(extraConfiguration);
    this.jumbotron = jumbotron;

    this.debounceModule = new DebounceModule();


    this.initialize();
  }

  initialize() {
    window.addEventListener("load", () => {
      const jumbotrons = document.querySelectorAll(".jumbotron");
      jumbotrons.forEach((jumbotron) => {
        this.measureJumbotronContent(jumbotron);
        this.setupVideoMeasuring(jumbotron);
      });
    });

    // Debounce resize events to avoid performance issues
    window.addEventListener('resize',
      this.debounceModule.debounce(() => this.measureJumbotronContent(), 100)
    );
  }

  measureJumbotronContent(jumbotron) {

    if (jumbotron) {
      let jumbotronVideos = jumbotron.querySelectorAll(".jumbotron-video");
      if (jumbotronVideos) {
        const visibleVideos = Array.from(jumbotronVideos)
          .filter(jumbotron => this.isElementVisible(jumbotron))
          .map(jumbotron => jumbotron.querySelector("video"))
          .filter(Boolean);

        if (visibleVideos.length > 0) {
          visibleVideos.forEach(video => {
            if (video.readyState >= 2) {
              this.measureVideo(video, jumbotron).then(() => {
                // console.log('measured video at video ready state 2 or greater');
              });
            } else {
              video.addEventListener("loadeddata", () => {
                this.measureVideo(video, jumbotron).then(() => {
                  // console.log('measured video after video data loaded');
                });
              }, { once: true });
            }
          });
          return;
        }
      }
    }

    if (jumbotron) {
      // Fix: Use getAttribute instead of dataset to be more reliable
      const backgroundElements = Array.from(jumbotron.querySelectorAll("[data-bg]"))
        .filter(element => this.isElementVisible(element) && element.getAttribute("data-image"));

      if (backgroundElements.length > 0) {
        backgroundElements.forEach(backgroundElement =>
          this.measureImage(backgroundElement, jumbotron));
      }
    }
  }

  setupVideoMeasuring(jumbotron) {
    if (jumbotron) {
      const videoElements = jumbotron.querySelectorAll("video");

      videoElements.forEach((video) => {
        video.play();

        if (!video.previousTimerUpdate) {
          video.previousTimerUpdate = 0;
        }

        if (!video.timeUpdateHandler) {
          video.timeUpdateHandler = () => {
            if (Math.abs(video.currentTime - video.previousTimerUpdate) > 5 || !video.previousTimerUpdate) {
              video.previousTimerUpdate = video.currentTime;
              this.measureVideo(video, jumbotron).then((v) => {
                // console.log('video measured after timeupdate');
              });
            }
          };
        }

        video.addEventListener("timeupdate", video.timeUpdateHandler);
        this.measureVideo(video, jumbotron).then(() => {
          // console.log('initial video measurement');
        });
      });
    }
  }

  async measureVideo(video, jumbotron) {
    // console.log('measureVideo', video);
    const videoContainer = video.closest(".jumbotron-video");

    if (videoContainer && !this.isElementVisible(videoContainer)) {
      return;
    }

    let cachedVideo = document.createElement('video');
    cachedVideo = video; // assign the actual video to a local cached video property

    cachedVideo.width = parseInt(video.videoWidth * 0.01);
    cachedVideo.height = parseInt(video.videoHeight * 0.01);

    // debug: document.documentElement.insertAdjacentElement('beforeend', cachedVideo);

    let canvas = document.createElement("canvas");
    canvas.width = cachedVideo.width;
    canvas.height = cachedVideo.height;

    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, cachedVideo.width, cachedVideo.height);

    const imageData = context.getImageData(
      0, 0, canvas.width, canvas.height,
      { willReadFrequently: true }
    );

    canvas = null; // unset, marking it garbage collectable

    const [r, g, b] = extractAvgColor(imageData);
    await this.onMeasured([r, g, b], jumbotron);

  }

  isElementVisible(element) {
    if (!element) return false;

    const computedStyle = window.getComputedStyle(element);

    if (computedStyle.display === 'none') return false;
    if (computedStyle.visibility === 'hidden') return false;

    const isVideo = element.tagName === 'VIDEO' || element.querySelector('video');

    if (!isVideo && computedStyle.backgroundImage === 'none' && !element.getAttribute('data-bg')) {
      return false;
    }

    return true;
  }

  // Override from parent class
  measureImage(imageElement, element) {
    if (!this.isElementVisible(imageElement)) return;

    super.measureImage(imageElement, element);
  }

  async onMeasured(rgb, element) {
    super.onMeasured(rgb, element).then((mode) => {
      // console.log('mode...', mode);
      let firstJumbo = document.querySelector('.jumbotron');
      if (firstJumbo) {
        // console.log('firstJumbo', firstJumbo, 'and element', element)
        if (element == firstJumbo) {
          // console.log('same, will set header theme');
          this.setTheme(document.querySelector("header"), mode);
        }
        if (element.classList.contains('jumbotron')) {
          this.setTheme(element, mode);
        }
      }
    });
  }
}